import { ethers, type BytesLike } from "ethers5";
import { Client, Presets, Utils } from "userop";
import { env } from "~/env";

export class AaHandler {
  private client?: Client;
  private simpleAccount?: Presets.Builder.SimpleAccount;
  private provider: ethers.providers.JsonRpcProvider;

  constructor() {
    this.provider = new ethers.providers.JsonRpcProvider(
      env.NEXT_PUBLIC_AA_RPC_URL,
    );
  }

  public static async create(privateKey: string): Promise<AaHandler> {
    if (!privateKey?.startsWith("0x")) {
      privateKey = "0x" + privateKey;
    }

    if (!AaHandler.isPrivateKeyValid(privateKey)) {
      throw new Error("Invalid private key");
    }

    const handler = new AaHandler();
    await handler.init(privateKey);

    return handler;
  }

  private static isPrivateKeyValid(privateKey: string): boolean {
    return (
      ethers.utils.isHexString(privateKey) &&
      ethers.utils.hexDataLength(privateKey) === 32
    );
  }

  public getWalletAddress(): string | undefined {
    return this.simpleAccount?.getSender();
  }

  public async isWalletDeployed(): Promise<boolean> {
    const walletAddress = this.getWalletAddress();
    if (!walletAddress) {
      throw new Error("Invalid wallet address");
    }
    const code = await this.provider.getCode(walletAddress);

    return code !== "0x";
  }

  public async deployWallet() {
    if (!this.simpleAccount || !this.client) {
      throw new Error("SimpleAccount or Client is not initialized");
    }

    const resp = await this.client.sendUserOperation(
      this.simpleAccount.execute(env.NEXT_PUBLIC_PAY_MASTER_ADDRESS, "0", "0x"),
      {
        onBuild: (op: any) =>
          console.log(`Signed UserOperation: ${JSON.stringify(op)}`),
      },
    );
    console.log("deployWallet resp: ", resp.userOpHash);
    const ev = await resp.wait();

    console.log("deployWallet ev: ", ev);

    return ev?.transactionHash;
  }

  public async sendUserOperationEth(to: string, value: bigint) {
    if (!this.simpleAccount || !this.client) {
      throw new Error("SimpleAccount or Client is not initialized");
    }

    console.log("to: ", to, "value: ", value);

    const resp = await this.client.sendUserOperation(
      this.simpleAccount.execute(to, value, "0x"),
      {
        onBuild: (op: any) =>
          console.log(`Signed UserOperation: ${JSON.stringify(op)}`),
      },
    );
    console.log("sendUserOperationEth resp: ", resp.userOpHash);
    const ev = await resp.wait();

    console.log("sendUserOperationEth ev: ", ev);

    return ev?.transactionHash;
  }

  public async sendUserOperation(to: Array<string>, data: Array<BytesLike>) {
    if (!this.simpleAccount || !this.client) {
      throw new Error("SimpleAccount or Client is not initialized");
    }

    console.log("to: ", to, "data: ", data);

    const resp = await this.client.sendUserOperation(
      this.simpleAccount.executeBatch(to, data),
      {
        onBuild: (op: any) =>
          console.log(`Signed UserOperation: ${JSON.stringify(op)}`),
      },
    );
    console.log("sendUserOperation resp: ", resp.userOpHash);
    const ev = await resp.wait();

    console.log("sendUserOperation ev: ", ev);

    return ev?.transactionHash;
  }

  private async init(privateKey: string): Promise<void> {
    this.client = await Client.init(env.NEXT_PUBLIC_AA_RPC_URL ?? "", {
      entryPoint: env.NEXT_PUBLIC_ENTRY_POINT_ADDRESS,
      overrideBundlerRpc: env.NEXT_PUBLIC_BUNDLER_RPC_URL,
    });
    // The maximum amount of time to wait for the UserOperationEvent after calling response.wait()
    this.client.waitTimeoutMs = 1000 * 60 * 2;
    // The interval at which it will poll the node to look up UserOperationEvent.
    this.client.waitIntervalMs = 3000;

    this.simpleAccount = await Presets.Builder.SimpleAccount.init(
      new ethers.Wallet(privateKey),
      env.NEXT_PUBLIC_AA_RPC_URL ?? "",
      {
        paymasterMiddleware: this.paymasterMiddleware.bind(this),
        overrideBundlerRpc: env.NEXT_PUBLIC_BUNDLER_RPC_URL,
      },
    );
  }

  private async paymasterMiddleware(ctx: any): Promise<void> {
    const opJson = Utils.OpToJSON(ctx.op);
    const payMasterProvider = new ethers.providers.JsonRpcProvider(
      env.NEXT_PUBLIC_PAY_MASTER_RPC_URL,
    );
    const result = await payMasterProvider.send("pm_sponsorUserOperation", [
      opJson,
    ]);

    if (result.errorCode) {
      throw new Error(result.errorMsg);
    }

    ctx.op = result;
  }
}
